import { decimalToCents } from "@/features/staff/reports/charts/series"
import type { DayTotal } from "@/features/staff/reports/types"

/**
 * The shape of the period, beside the period's total.
 *
 * This is the KPI promotion #391 asked for, and it is deliberately NOT a KPI
 * band. All four headline figures — net sales, average ticket, shifts, average
 * per shift — are already on this card in `ReportFigures`. A band above them
 * would print two of them a second time in a heavier weight, which is the exact
 * failure `sales-block.tsx`'s own header comment exists to prevent: two totals
 * in the same visual weight, differing because one counts payments and the other
 * counts dishes, is how a reader stops trusting both. So the headline gets a
 * trend, not a duplicate.
 *
 * Neutral ink, never a hue: a single series has no identity to encode, and a
 * sales line drawn in red reads as an alarm the moment it goes down.
 *
 * `aria-hidden`, because the figure beside it is the accessible value and the
 * full series is already tabled under the day chart further down the card. A
 * decorative polyline announced to a screen reader is noise.
 *
 * Under two service days there is no shape to draw and it renders nothing —
 * `today` and `yesterday` are one point, and a one-point "trend" is a claim.
 */
export function ReportSalesSparkline({ rows }: { rows: DayTotal[] }) {
  if (rows.length < 2) return null

  // Cents, so the polyline is plotted off the same integers everything else on
  // this screen is built from. Nothing here is read as a figure.
  const values = rows.map((row) => decimalToCents(row.subtotal))
  const max = Math.max(...values)
  const min = Math.min(...values)
  const span = max - min

  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 60
      // A flat period sits on the midline instead of collapsing onto the floor.
      const y = span === 0 ? 11 : 20 - ((value - min) / span) * 18
      return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`
    })
    .join(" ")

  return (
    <svg
      viewBox="0 0 60 22"
      width="60"
      height="22"
      fill="none"
      aria-hidden="true"
      className="shrink-0 overflow-visible"
      data-testid="report-sales-sparkline"
    >
      <polyline
        points={points}
        className="stroke-foreground"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
